import { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useTokens,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { getContrastTextColor } from '../utils/luminance';

interface PageIntroProps {
  tokenPrefix?: string;
  blurbField?: string;
  defaultText?: string;
  ctaLabel?: string;
  ctaUrl?: string;
}

const backImageDef: SlotGroupDefinition = {
  label: 'Back Collage Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[2].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[2].alt_text',
      defaultValue: 'Apartment interior',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 2,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select a landscape-oriented interior photo — open kitchen, dining, or living area. Prefer bright, inviting rooms. Skip logos, floor plans, and exterior-only shots.',
    context:
      'Back plate of the overlapping collage on the page intro section; pairs with a taller foreground portrait, matching Property Overview image alignment.',
  },
};

const frontImageDef: SlotGroupDefinition = {
  label: 'Front Collage Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'photo_gallery[1].image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultField: 'photo_gallery[1].alt_text',
      defaultValue: 'Apartment living space',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 1,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
  generator: {
    prompt:
      'Select a taller interior lifestyle photo — living room, seating area, or styled residential vignette. Prefer a different room than the back collage image. Skip logos and floor-plan diagrams.',
    context:
      'Foreground overlapping plate on the page intro section; left-overlaps the back image like Property Overview fg_image / bg_image.',
  },
};

function makeCtaLinkDef(ctaUrl: string, ctaLabel: string): SlotGroupDefinition {
  return {
    label: 'CTA Link',
    fields: {
      url: {
        label: 'URL',
        type: 'url',
        defaultValue: ctaUrl,
        suggestedFields: ['property_apply_now_url', 'property_website_url'],
      },
      label: {
        label: 'Link Text',
        type: 'text',
        defaultValue: ctaLabel,
      },
      _target: {
        label: 'Target',
        type: 'text',
        defaultValue: '_self',
      },
    },
  };
}

export function PageIntro({
  tokenPrefix,
  blurbField,
  defaultText,
  ctaLabel = 'View Floor Plans',
  ctaUrl = '/floor-plans',
}: PageIntroProps) {
  const prefix = tokenPrefix || 'pageintro';
  const blurbPath = `${prefix}.blurb`;
  const ctaPath = `${prefix}.cta_link`;
  const backPath = `${prefix}.back_image`;
  const frontPath = `${prefix}.front_image`;

  // Built via makeCtaLinkDef() so the schema-linter static extractor does not
  // pick up a free-variable object literal and fail eval of module-level defs.
  const ctaLinkDef = makeCtaLinkDef(ctaUrl, ctaLabel);

  const backImage = useSlotGroup(backPath, backImageDef);
  const frontImage = useSlotGroup(frontPath, frontImageDef);
  const cta = useSlotGroup(ctaPath, ctaLinkDef);

  const tokens = useTokens();
  const ctaHoverTextColor = useMemo(() => {
    const primary = tokens?.color?.primary;
    if (!primary) return null;
    return getContrastTextColor(primary);
  }, [tokens]);

  useRevealStyles();
  const contentRef = useScrollReveal<HTMLDivElement>();
  const imageRef = useScrollReveal<HTMLDivElement>();

  return (
    <section
      className="w-full overflow-x-hidden md:overflow-hidden bg-[#212222]"
      style={
        ctaHoverTextColor
          ? { ['--pageintro-cta-hover-text' as string]: ctaHoverTextColor }
          : undefined
      }
    >
      <div className="flex flex-col-reverse md:flex-row md:items-center max-w-[1400px] mx-auto">
        {/* ── Text column ── */}
        <div
          ref={contentRef}
          className="reveal-fade-up relative z-0 w-full md:w-[55%] lg:w-[58%] flex flex-col justify-center items-start text-left px-8 sm:px-12 md:px-10 lg:px-16 xl:px-24 pt-48 pb-14 sm:pt-52 md:py-20 lg:py-24"
        >
          <RichTextContent
            path={blurbPath}
            defaultField={blurbField}
            defaultValue={defaultText || ''}
            as="div"
            className="font-body text-[15px] sm:text-base md:text-[17px] leading-[1.7] text-white/90 max-w-lg mb-9 [&_a]:underline [&_p]:mb-4 last:[&_p]:mb-0"
          />

          <EditableSlotGroup groupPath={ctaPath} definition={ctaLinkDef}>
            <a
              href={cta.url || '#'}
              target={cta._target || '_self'}
              className="pageintro-cta group/cta relative inline-flex items-center gap-2.5 px-8 py-3.5 text-sm font-bold tracking-[0.12em] uppercase overflow-hidden transition-all duration-300"
            >
              <span className="relative z-10">{cta.label || ctaLabel}</span>
              <span
                className="relative z-10 text-lg leading-none transition-transform duration-300 group-hover/cta:translate-x-0.5"
                aria-hidden="true"
              >
                ›
              </span>
            </a>
          </EditableSlotGroup>
        </div>

        {/* ── Overlapping image composition — mirrors PropertyOverview ──
            Mobile: bg flush under banner; both plates hang into the text area.
            Desktop: right-shifted stack with left-overlapping fg. */}
        <div
          ref={imageRef}
          className="reveal-fade-up relative z-[1] w-full md:w-[45%] lg:w-[42%] h-[280px] sm:h-[320px] md:h-[480px] lg:h-[540px] shrink-0 -mb-24 sm:-mb-28 md:mb-0"
        >
          <div className="absolute inset-x-0 top-0 md:inset-x-auto md:right-0 md:top-1/2 md:-translate-y-1/2 w-full md:w-[78%] lg:w-[82%] h-full md:h-[72%] z-[1]">
            <div className="absolute inset-0">
              <EditableSlotGroup groupPath={backPath} definition={backImageDef}>
                {backImage.image ? (
                  <VibesitesImage
                    url={backImage.image}
                    alt={backImage.alt || 'Apartment interior'}
                    sizes="(max-width: 768px) 100vw, 35vw"
                    className="absolute inset-0 w-full h-full object-cover object-center"
                  />
                ) : (
                  <div className="absolute inset-0 bg-white/10" />
                )}
              </EditableSlotGroup>
            </div>

            <div className="pageintro-fg absolute left-1/2 md:left-0 top-[78%] md:top-1/2 -translate-x-1/2 md:-translate-x-[42%] -translate-y-1/2 w-[70%] max-w-[260px] sm:max-w-[280px] md:max-w-none md:w-[60%] lg:w-[58%] aspect-[368/378] z-[2]">
              <div className="absolute inset-0">
                <EditableSlotGroup groupPath={frontPath} definition={frontImageDef}>
                  {frontImage.image ? (
                    <VibesitesImage
                      url={frontImage.image}
                      alt={frontImage.alt || 'Apartment living space'}
                      sizes="(max-width: 768px) 72vw, 22vw"
                      className="absolute inset-0 w-full h-full object-cover object-center"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-white/15" />
                  )}
                </EditableSlotGroup>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Schema-lint anchors — hidden, never interactive */}
      <RichTextContent
        path="pageintro.blurb"
        defaultValue=""
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="pageintro.cta_link"
        definition={ctaLinkDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="pageintro.back_image"
        definition={backImageDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup
        groupPath="pageintro.front_image"
        definition={frontImageDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>

      <style>{`
        .pageintro-cta {
          background: var(--vibe-color-secondary);
          border: none;
          border-radius: 2px;
          color: var(--vibe-color-text-on-secondary, #fff);
          box-shadow: none;
        }
        .pageintro-cta:hover {
          background: var(--vibe-color-primary);
          color: var(--pageintro-cta-hover-text, var(--vibe-color-text-on-primary, #fff));
          transform: translateY(-1px);
        }
        .pageintro-cta:active {
          transform: translateY(0);
        }
        .pageintro-fg {
          box-shadow: 2.58px -4.29px 18px rgba(84, 82, 79, 0.85);
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PageIntro',
  displayName: 'Page Intro',
  tokens: {
    'pageintro.blurb': {
      label: 'Page Blurb',
      type: 'richtext',
      defaultValue: '',
    },
  },
  slotGroups: {
    'pageintro.back_image': backImageDef,
    'pageintro.front_image': frontImageDef,
    'pageintro.cta_link': {
      label: 'CTA Link',
      fields: {
        url: {
          label: 'URL',
          type: 'url',
          defaultValue: '/floor-plans',
          suggestedFields: ['property_apply_now_url', 'property_website_url'],
        },
        label: {
          label: 'Link Text',
          type: 'text',
          defaultValue: 'View Floor Plans',
        },
        _target: {
          label: 'Target',
          type: 'text',
          defaultValue: '_self',
        },
      },
    },
  },
};

(PageIntro as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PageIntro;
